import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, BedDouble, Ban, Check, Home, Hotel, Loader2, Pencil, Undo2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import {
  formatFlightClock24,
  formatFlightDay,
  formatFlightWeekdayDay,
} from '@/lib/disruptions/airport-tz';
import type { ManualHold, Reservation } from '@/lib/booking/types';
import { cancelReservation, getReservations, releaseHeldReservation, releaseHold as releaseManualHold } from '@/lib/booking/api';
import { manualHoldsKey, useManualHolds } from '@/lib/booking/use-manual-holds';
import { useCaseNotifications, useForceAccept } from '@/lib/notifications/use-notifications';
import { HotelSearchDialog } from '@/routes/case-detail/allocation-tab/hotel-search-dialog';
import { usePendingHolds } from '@/routes/case-detail/allocation-tab/use-holds';
import { HoldRow } from '@/components/allocation/hold-row';
import { acceptanceTone, formatCountdown, holdClock } from '@/components/allocation/types';
import { StayMismatchNote, StayStrip } from '@/components/allocation/stay-line';
import { useNow } from '@/components/allocation/use-now';
import { RESERVATION_STATUS_TONE } from '@/components/allocation/reservation-status';
import { hotelProviderName } from '@/lib/hotels/vendor';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAllocateCaseHotel } from '@/lib/disruptions/use-disruptions';
import { BulkInProgressError, type DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { routingDisruptionUrn, type PnrGroupScope } from '@/lib/pnr/scope';
import type { OptionSegment } from '@/lib/rebooking/types';
import { impliesOvernight } from '@/routes/disruptions/pnr-status';
import {
  HOTEL_CHECK_IN_HOUR,
  HOTEL_CHECK_OUT_HOUR,
  STAY_NIGHTS_MAX,
  STAY_NIGHTS_MIN,
  hotelClock,
  stayWindow,
  windowAsPlan,
} from '@/lib/allocation/stay';
import {
  useDeclareStayHome,
  useGroupStay,
  useRevertStayHome,
  useSetGroupNights,
} from '@/lib/allocation/use-stay';
import { newIdempotencyKey } from '@/lib/uuid';
import { cn } from '@/lib/cn';
import { FIELD_LABEL_CLASS } from './airport-tokens';
import { bedGuests, splitIntoRooms } from './room-split';

// Hotel & accommodation for the PNR — extracted verbatim from the roster drawer's
// HotelSection (all logic + testids intact). When a hotel is reserved (the
// sub-case denormalises `hotelReservationUrn`), we show the LIVE reservation
// matched out of the case's reservations by URN — name + status badge + rooms +
// total. The reservations query is keyed `['cases', caseUrn, 'reservations']`, the
// SAME key the WS `CaseChanged(kind:'booking')` invalidates, so the status settles
// live (no poll). Without a reservation the NEED is derived from the assigned
// flight's schedule (next-day → overnight); the actual allocation + booking + meal
// vouchers run in the case's Allocation flow.

export function HotelCard({
  group,
  scope,
  isSelected,
  selectedSegments,
  overnightAtHub,
  connectionUnvalidated,
  careOnly,
}: {
  group: DisruptionPnrGroupRow;
  scope: PnrGroupScope;
  /** Live (from the per-group options query) — a re-accommodation IS chosen. */
  isSelected: boolean;
  /** The chosen option's legs (live from the options query) — its first leg's
   *  departure is the assigned re-accommodation instant, available immediately
   *  (before the async `requiresHotel` / `assignedFlight` denorm lands). */
  selectedSegments?: OptionSegment[] | undefined;
  /** The selected option only reaches a connection HUB and the wait there for the
   *  surviving onward leg crosses a night — so "same-day, no overnight" is FALSE
   *  even when the re-accom leg itself departs same-day. Forces the hotel need. */
  overnightAtHub?: boolean | undefined;
  /** The selected option protects to a hub but the onward leg's schedule is unknown
   *  (mock manifest) — we can't claim "no overnight"; show an unvalidated note. */
  connectionUnvalidated?: boolean | undefined;
  /** The CASE is care-only (`rebookingDisabled`): the stay is an OFFERING —
   *  same facts + actions, calmer words (never "Hotel needed"). */
  careOnly?: boolean | undefined;
}) {
  const { t } = useTranslation('pnr');
  // The WS-invalidated reservations slice for this case (apply-event.ts §booking).
  const reservationsQuery = useQuery({
    queryKey: ['cases', group.caseUrn, 'reservations'],
    queryFn: () => getReservations(group.caseUrn),
    enabled: !!group,
  });
  // Allocation is an EVENT/airport-level action scoped to this case (cross-case pooling
  // in booking), not the old per-case manual flow. The one-click auto-allocate routes
  // through the PNR's parent disruption (the bulk endpoint is disruption-scoped) — the
  // scope's URN at disruption scope, the row's parent at case scope. Absent for a
  // standalone case → the Auto-allocate button is hidden (Choose-a-hotel still works).
  const allocate = useAllocateCaseHotel(scope);
  const routingUrn = routingDisruptionUrn(scope, group);
  const qc = useQueryClient();
  // Manual hotel pick + book — the "choose a specific hotel" exception path that
  // sits alongside the one-click auto allocate. Reuses the case-detail hold state
  // machine (hold → confirm/book → release) and the hotel-search dialog against
  // THIS PNR's case/sub-case. The booking endpoints auto-acquire the sub-case lock
  // server-side, so the operator works the PNR they already opened. Holds are the
  // UI's own state; once confirmed, the durable reservation settles into the list
  // above over the case WS room.
  const holdState = usePendingHolds(group.caseUrn);
  const myHolds = holdState.holds.filter((h) => h.subCaseUrn === group.subCaseUrn);
  const serverHolds = useManualHolds(group.caseUrn, group.subCaseUrn);
  const recoveredHolds = (serverHolds.data ?? []).filter((hold) =>
    !myHolds.some((local) => local.attemptUrn === hold.attemptUrn));
  const recoveredRelease = useMutation({
    mutationFn: (hold: ManualHold) => releaseManualHold(hold.offerUrn, group.caseUrn, group.subCaseUrn),
    onSuccess: (result, hold) => {
      if (result.released) qc.setQueryData<ManualHold[]>(manualHoldsKey(group.caseUrn, group.subCaseUrn),
        (previous) => previous?.filter((item) => item.attemptUrn !== hold.attemptUrn));
      void qc.invalidateQueries({ queryKey: manualHoldsKey(group.caseUrn, group.subCaseUrn) });
    },
  });
  const now = useNow(myHolds.length > 0);
  // Hotels are needed where the passenger is stranded — the disrupted leg's origin
  // for the common cancelled-departure case. Rooms ≈ 1 per 2 pax (mirrors
  // estimateRooms); the search is policy-ranked server-side per the case.
  // Search hotels where the night actually FALLS: the verdict's overnight station
  // (the connection hub for a night-crossing layover), not blindly the stranding
  // airport — a PMC pax overnighting in SCL needs an SCL room offered here.
  const airportCode = group.overnightStation ?? group.origin;
  const roomsRequired = Math.max(1, Math.ceil((group.paxCount || 1) / 2));
  // The drawer works one PNR the operator opened; the BFF auto-acquires its
  // sub-case lock on book, so Confirm is presented as available — a real
  // cross-operator conflict surfaces as a 409 the hold card shows.
  const manualLock = { isMine: true, isHeld: true, holderName: null, expiresAt: null };
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const cancel = useMutation({
    mutationFn: (reservationUrn: string) =>
      cancelReservation(
        reservationUrn,
        'OPERATOR_CANCEL',
        newIdempotencyKey(),
        group.subCaseUrn,
        group.caseUrn,
      ),
    onSuccess: () => {
      setConfirmingCancel(false);
      void qc.invalidateQueries({ queryKey: ['cases', group.caseUrn, 'reservations'] });
    },
  });
  // The STAY-HOME declaration (GOAL 2, any case type): the passenger disrupted
  // in their own town keeps their rebooking but needs no hotel. Scope-aware
  // like the nights write; undoable; hidden while a room is HELD/BOOKED.
  const stayHomeScope = routingUrn
    ? { disruptionUrn: routingUrn }
    : { caseUrn: group.caseUrn };
  const stayingHome = group.passengerElection === 'LOCAL_RESIDENT';
  const [confirmingStayHome, setConfirmingStayHome] = useState(false);
  const [stayHomeNote, setStayHomeNote] = useState('');
  // The home address (ride-home vertical): typed once here, geocoded by cases.
  // Optional — a passenger who says "I live here" needs no hotel with or without
  // a routable address; without one the transport card simply offers no ride home.
  const [stayHomeAddress, setStayHomeAddress] = useState('');
  const homeDestination = stayingHome ? group.homeDestination : undefined;
  const declareHome = useDeclareStayHome(stayHomeScope, group.subCaseUrn);
  const revertHome = useRevertStayHome(stayHomeScope, group.subCaseUrn);
  // Match this PNR's reservation by SUB-CASE urn first — it's stable and present
  // the moment the reservations list refreshes (over the case WS room), unlike
  // `group.hotelReservationUrn`, which is denormalised onto the roster row ASYNC
  // and is snapshot-stale on the open drawer right after allocation. Fall back to
  // the denorm urn. Ignore terminal (cancelled/failed) rows so a stale cancel
  // doesn't read as "reserved"; the list is newest-first so the active one wins.
  const confirmedReservationUrn = holdState.confirmed[group.subCaseUrn]?.reservationUrn;
  const confirmedReservation = reservationsQuery.data?.find(
    (r) => r.reservationUrn === confirmedReservationUrn,
  );
  const reservation = reservationsQuery.data?.find(
    (r) =>
      (r.subCaseUrn === group.subCaseUrn ||
        (!!group.hotelReservationUrn && r.reservationUrn === group.hotelReservationUrn) ||
        (!!confirmedReservationUrn && r.reservationUrn === confirmedReservationUrn)) &&
      r.status !== 'CANCELLED' &&
      r.status !== 'FAILED' &&
      r.status !== 'CANCEL_FAILED',
  );
  // Manual soft-holds live outside reservations until confirmed. Keep the
  // current choice exclusive, including the brief confirm → durable-row handoff.
  const waitingForReservation = !!confirmedReservationUrn && !confirmedReservation;
  const hotelChangeBlocked = !!reservation || myHolds.length > 0 || waitingForReservation ||
    recoveredHolds.length > 0 || serverHolds.isPending || serverHolds.isError;
  const hotelChangeBlockedRef = useRef(hotelChangeBlocked);
  hotelChangeBlockedRef.current = hotelChangeBlocked;
  useEffect(() => {
    if (hotelChangeBlocked) setConfirmingStayHome(false);
  }, [hotelChangeBlocked]);

  // `requiresHotel` is denormalised onto the roster row by cases ASYNC (rebooking
  // → bus → cases derives it), and the drawer holds a snapshot of the row from
  // open time — so right after the operator selects a flight it's still
  // `undefined` here even though a re-accommodation IS chosen. We must NOT just
  // assume "hotel needed" then: a SAME-DAY reflight needs no hotel, and showing
  // "Overnight — needs a hotel" + an Allocate button for it is the bug. Instead we
  // derive the overnight need locally from the chosen flight's departure vs the
  // disrupted day — mirroring the backend `deriveHotelRequirement` (rebooking.tds
  // §12) — as a PROVISIONAL value until the authoritative `requiresHotel`
  // reconciles on the next roster load. The assigned departure is read from the
  // live option segments (present immediately), falling back to the (async)
  // denormalised assignedFlight.
  const assignedDepartureIso =
    selectedSegments?.[0]?.departure ?? group.assignedFlight?.departureAt;
  const provisionalOvernight =
    isSelected && group.requiresHotel === undefined
      ? impliesOvernight(group.departureAt, assignedDepartureIso)
      : undefined;
  // A connection option whose HUB wait crosses a night needs a hotel even though its
  // re-accom leg departs "same-day" — the leg-1-departure heuristic above is blind to
  // the hub wait, so fold the authoritative `overnightAtHub` in here.
  const needs = group.requiresHotel === true || provisionalOvernight === true || overnightAtHub === true;
  // Don't claim "same-day, no overnight" when the hub wait is overnight OR the onward
  // connection's schedule isn't known yet — that was the misleading "no hotel" message.
  const sameDay =
    !overnightAtHub &&
    !connectionUnvalidated &&
    (group.requiresHotel === false || provisionalOvernight === false);
  // Selected, but we can't yet tell whether it's overnight (the option segments
  // haven't loaded and nothing is denormalised, or the onward leg is unvalidated) —
  // a transient state; show a neutral "confirming" line rather than a wrong verdict.
  const confirming = isSelected && !needs && !sameDay;

  // The card's ACTIONS live on its bar (the header's right edge) — allocate /
  // choose while a room is needed, cancel once one is booked. A HELD room's two
  // verbs (confirm / release) live with its countdown instead, inside the
  // reservation: they answer that clock. Two allocation paths: AUTO (one-click,
  // the domain pools the airport's cases and books the cheapest-compliant hotel)
  // and MANUAL (search → pick a specific hotel → soft-hold → confirm). Auto is
  // the fast default; manual is the operator's override.
  const headerActions =
    reservation && reservation.status === 'BOOKED' && !confirmingCancel ? (
      <Button
        size="sm"
        variant="outline"
        className="text-destructive hover:text-destructive"
        onClick={() => setConfirmingCancel(true)}
        data-testid="drawer-hotel-cancel"
      >
        <Ban className="mr-1 h-3.5 w-3.5" aria-hidden />
        {t('hotel.cancelReservation')}
      </Button>
    ) : !reservation && needs ? (
      <>
        {routingUrn ? (
          <Button
            size="sm"
            onClick={() => {
              if (hotelChangeBlockedRef.current) return;
              allocate.mutate({
                caseUrn: group.caseUrn,
                subCaseUrn: group.subCaseUrn,
                disruptionUrn: routingUrn,
              });
            }}
            disabled={allocate.isPending || hotelChangeBlocked}
            data-testid="drawer-allocate-hotel"
          >
            {allocate.isPending ? (
              <>
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />{' '}
                {t('hotel.allocating')}
              </>
            ) : (
              <>
                {t('hotel.autoAllocate')}{' '}
                <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
              </>
            )}
          </Button>
        ) : null}
        {hotelChangeBlocked ? (
          // Do not mount the dialog behind a disabled trigger: its wrapper is
          // independently clickable, and an already-open search must close.
          <Button size="sm" variant="outline" disabled data-testid="drawer-choose-hotel">
            <BedDouble className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('hotel.chooseHotel')}
          </Button>
        ) : (
          <HotelSearchDialog
            caseUrn={group.caseUrn}
            subCaseUrn={group.subCaseUrn}
            airportCode={airportCode}
            roomsRequired={roomsRequired}
            paxCount={group.paxCount}
            onHold={async (...args) => {
              if (hotelChangeBlockedRef.current) return;
              await holdState.placeHold(...args);
              void serverHolds.refetch();
            }}
            trigger={
              <Button size="sm" variant="outline" data-testid="drawer-choose-hotel">
                <BedDouble className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('hotel.chooseHotel')}
              </Button>
            }
          />
        )}
      </>
    ) : null;
  // The STAY-HOME verb (GOAL 2): offered while no room is HELD/BOOKED/PENDING
  // and the passenger is not already declared home. Any case type.
  const stayHomeAction =
    !reservation && !stayingHome && !confirmingStayHome ? (
      <Button
        size="sm"
        variant="outline"
        disabled={hotelChangeBlocked}
        onClick={() => setConfirmingStayHome(true)}
        data-testid="drawer-stay-home"
      >
        <Home className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('hotel.stayHome.action')}
      </Button>
    ) : null;
  return (
    <Card data-testid="drawer-hotel">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 p-4 pb-2">
        <CardTitle className={cn('flex items-center gap-1.5', FIELD_LABEL_CLASS)}>
          <Hotel className="h-3.5 w-3.5" aria-hidden /> {t('hotel.title')}
        </CardTitle>
        {headerActions || stayHomeAction ? (
          <div className="flex flex-wrap items-center gap-2" data-testid="drawer-hotel-actions">
            {headerActions}
            {stayHomeAction}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <HotelArrivalStatus group={group} />
        {/* Stay-home confirm (GOAL 2) — the card idiom's inline confirm box. */}
        {serverHolds.isPending || serverHolds.isError ? (
          <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground" role="status" data-testid="drawer-hotel-holds-status">
            <span>{t(serverHolds.isError ? 'hotel.recovery.unavailable' : 'hotel.recovery.checking')}</span>
            {serverHolds.isError ? <Button size="sm" variant="outline" onClick={() => void serverHolds.refetch()} disabled={serverHolds.isFetching}>
              {t('notification.retry')}
            </Button> : null}
          </div>
        ) : null}
        {myHolds.length > 0 || recoveredHolds.length > 0 ? (
          <p className="mb-2 text-xs text-muted-foreground" data-testid="drawer-hotel-change-blocked">
            {t('hotel.releaseBeforeChange')}
          </p>
        ) : waitingForReservation ? (
          <p className="mb-2 text-xs text-muted-foreground">{t('hotel.allocating')}</p>
        ) : null}
        {confirmingStayHome && !hotelChangeBlocked ? (
          <div
            className="mb-2 space-y-2 rounded-md border border-border bg-muted/30 p-2.5 text-sm"
            data-testid="drawer-stay-home-confirm"
          >
            <p className="text-muted-foreground">{t('hotel.stayHome.body')}</p>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('hotel.stayHome.noteLabel')}</span>
              <input
                type="text"
                value={stayHomeNote}
                onChange={(e) => setStayHomeNote(e.target.value)}
                placeholder={t('hotel.stayHome.notePlaceholder')}
                className="w-full rounded border border-input bg-background px-2 py-1"
                data-testid="drawer-stay-home-note"
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-muted-foreground">{t('hotel.stayHome.addressLabel')}</span>
              <input
                type="text"
                value={stayHomeAddress}
                onChange={(e) => setStayHomeAddress(e.target.value)}
                placeholder={t('hotel.stayHome.addressPlaceholder')}
                maxLength={300}
                autoComplete="off"
                className="w-full rounded border border-input bg-background px-2 py-1"
                data-testid="drawer-stay-home-address"
              />
              <span className="mt-1 block text-[11px] text-muted-foreground">
                {t('hotel.stayHome.addressHint')}
              </span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={declareHome.isPending}
                onClick={() => {
                  if (hotelChangeBlockedRef.current) return;
                  const note = stayHomeNote.trim();
                  const homeAddress = stayHomeAddress.trim();
                  declareHome.mutate(
                    note || homeAddress
                      ? { ...(note ? { note } : {}), ...(homeAddress ? { homeAddress } : {}) }
                      : undefined,
                    {
                      onSuccess: () => {
                        setConfirmingStayHome(false);
                        setStayHomeNote('');
                        setStayHomeAddress('');
                      },
                    },
                  );
                }}
                data-testid="drawer-stay-home-go"
              >
                {declareHome.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Home className="mr-1 h-3.5 w-3.5" aria-hidden />
                )}
                {t('hotel.stayHome.confirm')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmingStayHome(false)}>
                {t('hotel.stayHome.keep')}
              </Button>
            </div>
            {declareHome.isError ? (
              <p className="text-xs text-destructive" data-testid="drawer-stay-home-error">
                {t('hotel.stayHome.error')}
              </p>
            ) : null}
          </div>
        ) : null}
        {/* Live soft-holds for this PNR (the manual pick flow) — each offers
            Confirm booking / Release, with the TTL countdown. Shown above whatever
            branch renders below so a hold the operator just placed is always visible. */}
        {myHolds.length > 0 ? (
          <ul className="mb-2 space-y-2" data-testid="drawer-hotel-holds">
            {myHolds.map((h) => (
              <li key={h.id}>
                <HoldRow
                  hold={h}
                  now={now}
                  groupLabel={group.pnrLabel}
                  lock={manualLock}
                  onConfirm={() => { void (async () => { await holdState.confirm(h); void serverHolds.refetch(); })(); }}
                  onRelease={() => { void (async () => { await holdState.release(h); void serverHolds.refetch(); })(); }}
                  onRehold={() => void holdState.rehold(h)}
                  onDismiss={() => holdState.dismiss(h)}
                />
              </li>
            ))}
          </ul>
        ) : null}
        {recoveredHolds.length > 0 ? (
          <ul className="mb-2 space-y-2" data-testid="drawer-hotel-recovered-holds">
            {recoveredHolds.map((hold) => (
              <li key={hold.attemptUrn} className="rounded-md border border-border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{hold.hotelName || t('hotel.thisHotel')}</span>
                  <Badge variant="secondary">{t('hotel.recovery.held')}</Badge>
                  <Button size="sm" variant="outline" disabled={recoveredRelease.isPending}
                    onClick={() => recoveredRelease.mutate(hold)}>{t('hotel.releaseHold')}</Button>
                </div>
                {recoveredRelease.isError && recoveredRelease.variables?.attemptUrn === hold.attemptUrn ? (
                  <p className="mt-2 text-xs text-destructive" role="alert">{t('hotel.releaseHoldError')}</p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {/* HOW LONG this PNR stays — and the operator's own number, if they have
            one (feature `stay-nights`, N8). Only where a hotel is actually in
            play: a group with no overnight need has no stay to argue about.
            With a reservation the dates live IN it; the nights control folds
            behind a "change nights" link under it. */}
        {!reservation && (needs || myHolds.length > 0) ? (
          <StaySection group={group} disruptionUrn={routingUrn} />
        ) : null}
        {reservation ? (
          <div className="space-y-2">
            <ReservationSummary reservation={reservation} group={group} />
            <StaySection group={group} disruptionUrn={routingUrn} folded />
            {reservation.status === 'BOOKED' ? (
              <div className="pt-1">
                {confirmingCancel ? (
                  <div
                    className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm"
                    data-testid="drawer-hotel-cancel-confirm"
                  >
                    <p className="text-muted-foreground">
                      {t('hotel.releaseAt')}{' '}
                      <span className="font-medium text-foreground">
                        {reservation.hotelName ?? t('hotel.thisHotel')}
                      </span>
                      {t('hotel.releaseBody')}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(reservation.reservationUrn)}
                        data-testid="drawer-hotel-cancel-go"
                      >
                        {cancel.isPending ? (
                          <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
                        ) : (
                          <Ban className="mr-1 h-3.5 w-3.5" aria-hidden />
                        )}
                        {t('hotel.releaseRoom')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmingCancel(false)}>
                        {t('hotel.keepReservation')}
                      </Button>
                    </div>
                    {cancel.isError ? (
                      <p className="text-xs text-destructive">{t('hotel.cancelError')}</p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : stayingHome ? (
          // Declared stay-home — calm, with the undo right where it was declared.
          <div
            className="flex flex-wrap items-center gap-2 text-sm"
            data-testid="drawer-hotel-stay-home"
          >
            <Badge variant="muted" size="sm">
              <Home className="h-3 w-3" aria-hidden /> {t('hotelBadge.ownHome')}
            </Badge>
            <span className="text-muted-foreground">{t('hotel.stayHome.declaredBody')}</span>
            {/* Where the ride home goes — the coarse label only (the street is
                behind an audited reveal). A declaration without an address says
                nothing here; one the geocoder could not place says so, because
                that is why the transport card offers no ride. */}
            {homeDestination ? (
              homeDestination.coordinates ? (
                <span className="text-muted-foreground" data-testid="drawer-stay-home-destination">
                  {homeDestination.label
                    ? t('hotel.stayHome.destination', { label: homeDestination.label })
                    : t('hotel.stayHome.destinationNoLabel')}
                </span>
              ) : (
                <span
                  className="text-muted-foreground"
                  data-testid="drawer-stay-home-destination-unresolved"
                >
                  {t('hotel.stayHome.destinationUnresolved')}
                </span>
              )
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] text-muted-foreground"
              disabled={revertHome.isPending}
              onClick={() => revertHome.mutate()}
              data-testid="drawer-stay-home-undo"
            >
              {revertHome.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Undo2 className="mr-1 h-3 w-3" aria-hidden />
              )}
              {t('hotel.stayHome.undo')}
            </Button>
            {revertHome.isError ? (
              <span className="text-xs text-destructive" data-testid="drawer-stay-home-undo-error">
                {t('hotel.stayHome.undoError')}
              </span>
            ) : null}
          </div>
        ) : needs ? (
          <div className="space-y-2">
            {/* Care-only (GOAL 1): the same facts and the same actions, but the
                stay reads as an OFFERING — the passenger may already have their
                own plans. `requiresHotel` stays true on the wire. */}
            <p className="flex flex-wrap items-center gap-1.5 text-sm">
              {careOnly ? (
                <Badge variant="info" size="sm" title={t('hotelBadge.optionalTitle')}>
                  {t('hotelBadge.optional')}
                </Badge>
              ) : (
                <Badge variant="warning" size="sm">
                  {t('hotelBadge.needed')}
                </Badge>
              )}
              <span className="text-muted-foreground">
                {careOnly
                  ? t('hotel.careOnlyOffer')
                  : group.overnightStation && group.overnightStation !== group.origin
                    ? t('hotel.overnightAtHub', { station: group.overnightStation })
                    : t('hotel.overnightNextDay')}
              </span>
            </p>
            {/* The Auto-allocate / Choose-a-hotel actions sit on the card's bar
                above; their feedback lands here, beside the need they answer. */}
            {allocate.isError ? (
              <p className="text-xs text-destructive" data-testid="drawer-allocate-error">
                {allocate.error instanceof BulkInProgressError
                  ? t('hotel.bulkInProgress')
                  : t('hotel.allocateError')}
              </p>
            ) : allocate.isSuccess ? (
              <p className="text-xs text-muted-foreground" data-testid="drawer-allocate-queued">
                {(allocate.data?.queuedCases ?? 0) > 0
                  ? t('hotel.allocateQueued')
                  : t('hotel.nothingToAllocate')}
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {t('hotel.footnoteA')}{' '}
              <span className="font-medium">{t('hotel.chooseHotel')}</span>
              {t('hotel.footnoteB')}
            </p>
          </div>
        ) : sameDay ? (
          <p
            className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground"
            data-testid="drawer-hotel-same-day"
          >
            <Badge variant="muted" size="sm">
              {t('hotelBadge.none')}
            </Badge>
            {t('hotel.sameDay')}
          </p>
        ) : confirming ? (
          <p
            className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground"
            data-testid="drawer-hotel-confirming"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            {t('hotel.confirming')}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">{t('hotel.pending')}</p>
        )}
      </CardContent>
    </Card>
  );
}

/** Countdown colour per acceptance-clock tone (`acceptanceTone`). */
const HOLD_TONE_TEXT: Record<ReturnType<typeof acceptanceTone>, string> = {
  neutral: 'text-foreground',
  warning: 'text-amber-700 dark:text-amber-300',
  error: 'text-destructive',
  muted: 'text-muted-foreground',
};
const HOLD_TONE_BAR: Record<ReturnType<typeof acceptanceTone>, string> = {
  neutral: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-destructive',
  muted: 'bg-muted-foreground/40',
};

/**
 * The reservation, read the way the front desk asks for it: WHICH hotel and in
 * what state, WHEN (hotel-local dates at the hotel's clock — never the instant
 * the stay was derived at), HOW MANY rooms and who sleeps in each — and, while
 * HELD, how long is left to make it real, with the verb that does it right
 * there. Rate, provider and reference shrink to one line each: they answer the
 * phone call, not the decision.
 */
function ReservationSummary({
  reservation,
  group,
}: {
  reservation: Reservation;
  group: DisruptionPnrGroupRow;
}) {
  const { t } = useTranslation('pnr');
  const { t: tNetwork } = useTranslation('network');
  const station = group.overnightStation ?? group.origin;
  const part = group.offer?.parts?.hotel;
  const stay = useGroupStay(group.caseUrn, group.subCaseUrn);
  const provider = hotelProviderName(tNetwork, reservation.hotelUrn);
  const money = (n: number, c: string): string =>
    n.toLocaleString(undefined, { style: 'currency', currency: c });
  // The row's amounts are the airline's price (commercial-contract §6.13); a row the BFF
  // could not price (`priced: false`, amounts null) reads «No disponible», never a number.
  const unpriced = (reservation as { priced?: boolean }).priced === false;
  const rate =
    typeof reservation.pricePerNight === 'number' && reservation.currency
      ? t('hotelCard.ratePerNight', { amount: money(reservation.pricePerNight, reservation.currency) })
      : typeof reservation.total === 'number' && reservation.currency
        ? t('hotelCard.rateTotal', { amount: money(reservation.total, reservation.currency) })
        : unpriced
          ? t('option.priceUnavailable')
          : null;
  // The window this room was held for: the reservation's own stay first (cases
  // denormalises booking's persisted check-in/out onto the offer's hotel part),
  // the group's live plan second. Only the part of THIS reservation counts — a
  // replaced reservation's dates are not this room's.
  const window =
    part?.checkInUtc &&
    part.checkOutUtc &&
    (!part.reservationUrn || part.reservationUrn === reservation.reservationUrn)
      ? { checkInUtc: part.checkInUtc, checkOutUtc: part.checkOutUtc, nights: part.nights }
      : stay.stayPlan
        ? {
            checkInUtc: stay.stayPlan.checkInUtc,
            checkOutUtc: stay.stayPlan.checkOutUtc,
            nights: stay.stayPlan.nights,
          }
        : undefined;
  // Nights are READ (cases / booking count hotel-local days), never re-derived.
  const nights = typeof window?.nights === 'number' && window.nights >= 1 ? window.nights : undefined;
  const passengers = group.passengers ?? [];
  const roomCount = reservation.rooms ?? part?.rooms;
  const rooms = splitIntoRooms(passengers, roomCount);
  const guests = reservation.guests ?? bedGuests(passengers);

  return (
    <div className="space-y-3" data-testid="drawer-hotel-reservation">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-base font-semibold leading-5" title={reservation.hotelName ?? reservation.hotelUrn}>
            {reservation.hotelName ?? reservation.hotelUrn}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="drawer-hotel-meta">
            <span title={t('hotelCard.provider')}>
              {provider ? t('hotelCard.via', { provider }) : t('hotelCard.providerUnknown')}
            </span>
            {rate ? <span className="tabular-nums"> · {rate}</span> : null}
          </p>
        </div>
        <Badge
          variant={RESERVATION_STATUS_TONE[reservation.status]}
          size="sm"
          className="shrink-0"
          data-testid="drawer-hotel-status"
        >
          {t(`hotelCard.status.${reservation.status}`)}
        </Badge>
      </div>

      <div
        className="grid grid-cols-[1fr_1fr_auto] gap-3 rounded-md border border-border bg-muted/30 px-3 py-2.5"
        data-testid="drawer-hotel-dates"
      >
        <StayEnd
          label={t('hotelCard.checkIn')}
          iso={window?.checkInUtc}
          station={station}
          clock={t('hotelCard.checkInFrom', { time: hotelClock(HOTEL_CHECK_IN_HOUR) })}
          testId="drawer-hotel-check-in"
        />
        <StayEnd
          label={t('hotelCard.checkOut')}
          iso={window?.checkOutUtc}
          station={station}
          clock={t('hotelCard.checkOutBy', { time: hotelClock(HOTEL_CHECK_OUT_HOUR) })}
          testId="drawer-hotel-check-out"
        />
        <div className="flex flex-col items-end gap-0.5">
          <span className={FIELD_LABEL_CLASS}>{t('hotelCard.nights')}</span>
          <span className="text-lg font-semibold leading-6 tabular-nums" data-testid="drawer-hotel-nights">
            {nights ?? '—'}
          </span>
        </div>
      </div>

      <div data-testid="drawer-hotel-rooms">
        <p className="flex flex-wrap items-baseline gap-x-1.5 text-sm">
          <span className="font-semibold">{t('hotel.rooms', { count: roomCount ?? rooms.length })}</span>
          <span className="text-muted-foreground">· {t('hotelCard.guests', { count: guests })}</span>
        </p>
        {passengers.length > 0 ? (
          <ol
            className="mt-1.5 divide-y divide-border rounded-md border border-border"
            title={t('hotelCard.splitHint')}
          >
            {rooms.map((r) => (
              <li
                key={r.room}
                className="flex items-start gap-3 px-3 py-1.5 text-sm"
                data-testid="drawer-hotel-room"
              >
                <span className="w-16 shrink-0 pt-px text-xs font-semibold text-muted-foreground">
                  {t('hotelCard.room', { n: r.room })}
                </span>
                {r.occupants.length ? (
                  <span className="flex min-w-0 flex-wrap gap-x-3 gap-y-0.5">
                    {r.occupants.map((o) => (
                      <span key={o.paxUrn} className="inline-flex min-w-0 items-center gap-1">
                        <span className="truncate">{o.name}</span>
                        {o.kind !== 'ADULT' ? (
                          <span className="rounded bg-muted px-1 text-[10px] font-medium uppercase text-muted-foreground">
                            {t(`hotelCard.kind.${o.kind}`)}
                          </span>
                        ) : null}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t('hotelCard.roomEmpty')}</span>
                )}
              </li>
            ))}
          </ol>
        ) : null}
      </div>

      {reservation.status === 'HELD' ? (
        <HeldPanel reservation={reservation} group={group} station={station} />
      ) : null}

      <p
        className="truncate text-[11px] text-muted-foreground"
        title={t('hotelCard.referenceHint')}
        data-testid="drawer-hotel-reference"
      >
        {t('hotelCard.reference')} <span className="font-mono">{reservation.reservationUrn}</span>
      </p>
    </div>
  );
}

/** One end of the stay: the hotel-local DATE big, the hotel's clock under it. */
function StayEnd({
  label,
  iso,
  station,
  clock,
  testId,
}: {
  label: string;
  iso: string | undefined;
  station: string | undefined;
  clock: string;
  testId: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-testid={testId}>
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <span className="truncate text-sm font-semibold">{iso ? formatFlightWeekdayDay(iso, station) : '—'}</span>
      {iso ? <span className="text-xs tabular-nums text-muted-foreground">{clock}</span> : null}
    </div>
  );
}

/**
 * A HELD room — the reversible offer the passenger has not accepted yet — as a
 * live clock with its two verbs on it.
 *
 * The clock is the COMMERCIAL acceptance deadline the reservation endpoint
 * states (#783) — never the offer part's `holdExpiresAt`, which may be a
 * different, recoverable clock — ticking every second, with the hold's start
 * and end on the hotel's clock. No deadline is "unknown", never "expired".
 *
 * CONFIRM is the per-PNR accept-on-behalf (SEAM-1 `OPERATOR_FORCE`, the BFF
 * gates it at AIRPORT_OPERATOR): the same canonical OfferAccepted that makes
 * booking confirm the room at the vendor, HELD → BOOKED, which then settles here
 * over the case WS room. RELEASE frees the room through the dedicated seam —
 * never `cancelReservation`, which booking silently no-ops for anything but
 * BOOKED (operator finding H). An expired clock disables neither: confirming
 * re-checks availability, and the room may still be there.
 */
function HeldPanel({
  reservation,
  group,
  station,
}: {
  reservation: Reservation;
  group: DisruptionPnrGroupRow;
  station: string | undefined;
}) {
  const { t } = useTranslation('pnr');
  const qc = useQueryClient();
  const now = useNow(true);
  const { rowFor } = useCaseNotifications(group.caseUrn);
  const row = rowFor(group.subCaseUrn);
  const offerHash = row?.offerHash ?? group.offer?.hash;
  const force = useForceAccept(group.caseUrn);
  const [step, setStep] = useState<'idle' | 'confirm' | 'release'>('idle');
  const release = useMutation({
    mutationFn: () => releaseHeldReservation(reservation.reservationUrn, group.caseUrn, group.subCaseUrn),
    onSuccess: () => {
      setStep('idle');
      void qc.invalidateQueries({ queryKey: ['cases', group.caseUrn, 'reservations'] });
    },
  });
  // Accepted — here or by the passenger — while booking confirms the vendor.
  const accepted = force.isSuccess || row?.acceptanceState === 'accepted';

  const clock = holdClock(reservation.acceptanceExpiresAt, now);
  const tone = acceptanceTone(clock);
  const deadline = clock.hasClock ? reservation.acceptanceExpiresAt : undefined;
  const heldAt =
    reservation.createdAt && Number.isFinite(Date.parse(reservation.createdAt)) ? reservation.createdAt : undefined;
  const spanMs = deadline && heldAt ? Date.parse(deadline) - Date.parse(heldAt) : NaN;
  const leftShare = Number.isFinite(spanMs) && spanMs > 0 ? Math.min(1, clock.msLeft / spanMs) : null;
  const deadlineLabel = deadline
    ? !heldAt || formatFlightDay(deadline, station) === formatFlightDay(heldAt, station)
      ? formatFlightClock24(deadline, station)
      : `${formatFlightDay(deadline, station)} ${formatFlightClock24(deadline, station)}`
    : null;

  return (
    <section
      className="space-y-2 rounded-md border border-amber-300/70 bg-amber-50/50 p-3 dark:border-amber-800/60 dark:bg-amber-950/20"
      aria-label={t('hotelCard.holdTitle')}
      data-testid="drawer-hotel-hold"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="min-w-0"
          role="timer"
          data-testid="acceptance-clock"
          data-clock={!clock.hasClock ? 'unknown' : clock.lapsed ? 'lapsed' : 'counting'}
          data-tone={tone}
        >
          <span className={FIELD_LABEL_CLASS}>{t('hotelCard.holdTitle')}</span>
          {clock.hasClock ? (
            <p className="flex flex-wrap items-baseline gap-x-2">
              <span className={cn('font-mono text-2xl font-semibold leading-8 tabular-nums', HOLD_TONE_TEXT[tone])}>
                {formatCountdown(clock.msLeft)}
              </span>
              <span className={cn('text-xs', clock.lapsed ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                {clock.lapsed ? t('hotelCard.holdLapsed') : t('hotelCard.holdLeft')}
              </span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('hotelCard.holdUnknown')}</p>
          )}
        </div>
        {accepted ? (
          <span
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
            role="status"
            data-testid="drawer-hotel-confirming"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> {t('hotelCard.confirming')}
          </span>
        ) : step === 'idle' ? (
          <div className="flex flex-wrap items-center gap-2">
            <RoleGate min={Role.AIRPORT_OPERATOR}>
              <Button
                size="sm"
                disabled={!offerHash}
                title={offerHash ? undefined : t('hotelCard.confirmNoOffer')}
                onClick={() => setStep('confirm')}
                data-testid="drawer-hotel-confirm"
              >
                <Check className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('hotelCard.confirm')}
              </Button>
            </RoleGate>
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive"
              onClick={() => setStep('release')}
              data-testid="drawer-hotel-release-hold"
            >
              <Ban className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('hotel.releaseHold')}
            </Button>
          </div>
        ) : null}
      </div>
      {leftShare !== null ? (
        <div className="h-1.5 overflow-hidden rounded-full bg-amber-200/60 dark:bg-amber-900/40" aria-hidden>
          <div
            className={cn('h-full rounded-full transition-[width] duration-1000 ease-linear', HOLD_TONE_BAR[tone])}
            style={{ width: `${leftShare * 100}%` }}
          />
        </div>
      ) : null}
      {heldAt || deadline ? (
        <p className="text-xs tabular-nums text-muted-foreground" data-testid="drawer-hotel-hold-window">
          {heldAt ? t('hotelCard.holdSince', { time: formatFlightClock24(heldAt, station) }) : null}
          {heldAt && deadline ? ' · ' : null}
          {deadline ? (
            <>
              {t('hotelCard.holdUntil')} <time dateTime={deadline}>{deadlineLabel}</time>
            </>
          ) : null}
        </p>
      ) : null}
      {clock.lapsed && !accepted ? (
        <p className="text-xs text-muted-foreground">{t('hotelCard.holdLapsedHint')}</p>
      ) : null}
      {step === 'confirm' && !accepted ? (
        <div
          className="space-y-2 rounded-md border border-border bg-background p-2.5 text-sm"
          data-testid="drawer-hotel-confirm-box"
        >
          <p className="text-muted-foreground">
            {t('hotelCard.confirmBody', { hotel: reservation.hotelName ?? t('hotel.thisHotel') })}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={force.isPending || !offerHash}
              onClick={() => {
                if (!offerHash) return;
                force.mutate(
                  { subCaseUrn: group.subCaseUrn, body: { offerHash } },
                  {
                    onSuccess: () => {
                      setStep('idle');
                      void qc.invalidateQueries({ queryKey: ['cases', group.caseUrn, 'reservations'] });
                    },
                  },
                );
              }}
              data-testid="drawer-hotel-confirm-go"
            >
              {force.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Check className="mr-1 h-3.5 w-3.5" aria-hidden />
              )}
              {t('hotelCard.confirmGo')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setStep('idle')}>
              {t('hotelCard.confirmBack')}
            </Button>
          </div>
          {force.isError ? (
            <p className="text-xs text-destructive" role="alert" data-testid="drawer-hotel-confirm-error">
              {t('hotelCard.confirmError')}
            </p>
          ) : null}
        </div>
      ) : null}
      {step === 'release' ? (
        <div
          className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm"
          data-testid="drawer-hotel-release-confirm"
        >
          <p className="text-muted-foreground">
            {t('hotel.releaseHoldAt')}{' '}
            <span className="font-medium text-foreground">
              {reservation.hotelName ?? t('hotel.thisHotel')}
            </span>
            {t('hotel.releaseHoldBody')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="destructive"
              disabled={release.isPending}
              onClick={() => release.mutate()}
              data-testid="drawer-hotel-release-go"
            >
              {release.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Ban className="mr-1 h-3.5 w-3.5" aria-hidden />
              )}
              {t('hotel.releaseHold')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setStep('idle')}>
              {t('hotel.keepHold')}
            </Button>
          </div>
          {release.isError ? (
            <p className="text-xs text-destructive" data-testid="drawer-hotel-release-error">
              {t('hotel.releaseHoldError')}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * The PNR's stay: how many nights, which dates, why that number — and the
 * operator's override (feature `stay-nights`, N8, the TOP rung of the §7 ladder).
 *
 * The override is a deliberate, per-PNR statement: it outranks every derived
 * rung AND any blanket number set for the whole disruption, so a group a human
 * has looked at is never re-derived out from under them. It is also CLEARABLE —
 * `nights: null` drops the group back onto the evidence rather than pinning
 * whatever happened to be derived last, which is the difference between "I know
 * better" and "I once looked at this".
 *
 * The write is scope-aware: a PNR under a disruption goes through the
 * disruption route; a STANDALONE case (no parent disruption — a hand-opened
 * care-only case, say) goes through the case-scoped twin. The control therefore
 * renders at BOTH scopes — the operator on a standalone case used to be stuck
 * with the 1-night default because only the read was offered here.
 *
 * `folded` (a reservation exists — its dates already read in the summary): only
 * the mismatch note and a "change nights" link until the operator asks for the
 * control; the strip is dropped so the dates are never said twice.
 */
function StaySection({
  group,
  disruptionUrn,
  folded = false,
}: {
  group: DisruptionPnrGroupRow;
  disruptionUrn: string | undefined;
  folded?: boolean;
}) {
  const { t } = useTranslation('pnr');
  const stay = useGroupStay(group.caseUrn, group.subCaseUrn);
  const setNights = useSetGroupNights(
    disruptionUrn ? { disruptionUrn } : { caseUrn: group.caseUrn },
    group.subCaseUrn,
  );
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(!folded);

  const parsed = Number.parseInt(draft, 10);
  const valid = Number.isInteger(parsed) && parsed >= STAY_NIGHTS_MIN && parsed <= STAY_NIGHTS_MAX;
  const overridden = stay.stayPlan?.source === 'OPERATOR_GROUP';
  // No plan is not an error and not zero nights — it is the DEFAULT rung, and
  // showing the window it WOULD book is more honest than showing nothing.
  const shown = stay.stayPlan ?? windowAsPlan(stayWindow(null));

  if (!open) {
    return (
      <div className="space-y-1.5" data-testid="drawer-hotel-stay">
        <StayMismatchNote
          mismatch={stay.stayMismatch}
          onResolve={(n: number) => setNights.mutate(n)}
          pending={setNights.isPending}
        />
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-[11px] text-muted-foreground"
          onClick={() => setOpen(true)}
          data-testid="drawer-stay-edit"
        >
          <Pencil className="mr-1 h-3 w-3" aria-hidden /> {t('hotelCard.editNights')}
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-2 space-y-1.5 rounded-md border border-border bg-muted/30 px-2.5 py-2" data-testid="drawer-hotel-stay">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <span className={FIELD_LABEL_CLASS}>{t('hotel.stay.title')}</span>
        {stay.isLoading ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> {t('hotel.stay.loading')}
          </span>
        ) : stay.isError ? (
          <span className="text-destructive">{t('hotel.stay.error')}</span>
        ) : null}
        <span className="ml-auto flex items-center gap-1.5">
          <label className="flex items-center gap-1">
            <span className="sr-only">{t('hotel.stay.setAria')}</span>
            <input
              type="number"
              inputMode="numeric"
              min={STAY_NIGHTS_MIN}
              max={STAY_NIGHTS_MAX}
              step="1"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && valid) setNights.mutate(parsed, { onSuccess: () => setDraft('') });
              }}
              placeholder={t('hotel.stay.placeholder')}
              aria-label={t('hotel.stay.setAria')}
              className="w-14 rounded border border-input bg-background px-1.5 py-0.5 tabular-nums"
              data-testid="drawer-stay-input"
            />
          </label>
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px]"
            disabled={!valid || setNights.isPending}
            onClick={() => setNights.mutate(parsed, { onSuccess: () => setDraft('') })}
            data-testid="drawer-stay-set"
          >
            {setNights.isPending ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
            ) : null}
            {t('hotel.stay.set')}
          </Button>
          {overridden ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] text-muted-foreground"
              disabled={setNights.isPending}
              onClick={() => setNights.mutate(null, { onSuccess: () => setDraft('') })}
              title={t('hotel.stay.clearTitle')}
              data-testid="drawer-stay-clear"
            >
              {t('hotel.stay.clear')}
            </Button>
          ) : null}
          {folded ? (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] text-muted-foreground"
              onClick={() => setOpen(false)}
              data-testid="drawer-stay-close"
            >
              {t('hotelCard.editNightsClose')}
            </Button>
          ) : null}
        </span>
      </div>
      {/* Date-first + timeline (operator finding G) — the same StayPlanView
          fields the old one-liner rendered, made legible: "Sep 2 → Sep 16 ·
          14 nights" with check-in / check-out labeled at the bar's ends. */}
      {!folded && !stay.isLoading && !stay.isError ? <StayStrip plan={shown} /> : null}
      {setNights.isError ? (
        <p className="text-xs text-destructive" data-testid="drawer-stay-error">
          {t('hotel.stay.saveError')}
        </p>
      ) : null}
      <StayMismatchNote
        mismatch={stay.stayMismatch}
        onResolve={(n: number) => setNights.mutate(n)}
        pending={setNights.isPending}
      />
    </div>
  );
}

/** Read-only facts; a reported problem is not an assertion that support was notified. */
function HotelArrivalStatus({ group }: { group: DisruptionPnrGroupRow }) {
  const { t } = useTranslation('pnr');
  const inquiries = group.arrivalInquiries ?? [];
  if (!inquiries.length) return null;
  const priority = { PROBLEM: 0, PENDING: 1, ARRIVED_OK: 2 };
  const sorted = inquiries.map((inquiry, index) => ({ inquiry, index }))
    .sort((a, b) => priority[a.inquiry.status] - priority[b.inquiry.status]);
  const hasProblem = inquiries.some((inquiry) => inquiry.status === 'PROBLEM');
  return (
    <section
      aria-label={t('hotel.arrival.title')}
      role={hasProblem ? 'alert' : 'status'}
      className={cn('mb-3 rounded-md border p-3 text-sm',
        hasProblem ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-muted/30')}
      data-testid="drawer-hotel-arrival"
    >
      <p className={cn('mb-2 font-medium', hasProblem && 'text-destructive')}>
        {t('hotel.arrival.title')}
      </p>
      <ul className="space-y-1.5">
        {sorted.map(({ inquiry, index }) => {
          const passenger = group.passengers.find((pax) => pax.paxUrn === inquiry.passengerUrn);
          return (
            <li key={inquiry.inquiryUrn} className="flex flex-wrap items-center justify-between gap-2">
              <span>{passenger?.fullName || t('hotel.arrival.passenger', { number: index + 1 })}</span>
              <span className={cn(inquiry.status === 'PROBLEM' ? 'font-medium text-destructive' : 'text-muted-foreground')}>
                {t(`hotel.arrival.${inquiry.status}`)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
